import type { ReactNode } from 'react';
import type { BookingStageKind } from '@peles/shared';
import { StatusBadge } from '../common/EntityList';
import { BOOKING_KIND_COLORS } from './bookingDisplay';

/** A booking's stage as a badge: its label, coloured by its kind. */
export function BookingStageBadge({
  stage,
}: {
  stage: { label: string; kind: BookingStageKind } | null | undefined;
}) {
  return stage ? <StatusBadge label={stage.label} color={BOOKING_KIND_COLORS[stage.kind]} /> : null;
}

/** A booking's money in a line, with a bar of how much is paid. */
export interface BookingCardMoney {
  /** "$4,950 · next due Oct 1" */
  text: string;
  /** 0–100 */
  paidPercent: number;
  /** Names the bar: "Bella Florals paid". */
  label: string;
}

export function BookingMoneyLine({ money }: { money: BookingCardMoney }) {
  return (
    <div className="space-y-1">
      <p className="text-gray-700">{money.text}</p>
      <div
        className="h-1.5 w-full max-w-40 rounded-full bg-gray-100"
        role="progressbar"
        aria-label={money.label}
        aria-valuenow={money.paidPercent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-1.5 rounded-full bg-green-500"
          style={{ width: `${money.paidPercent}%` }}
        />
      </div>
    </div>
  );
}

export interface BookingCardProps {
  /** The vendor's name — text, or the office's button onto the vendor. */
  title: ReactNode;
  /** "Flowers · 2 needs · arr. 3:00 PM" */
  description?: string;
  stage?: { label: string; kind: BookingStageKind } | null;
  /** A red badge under the title — an expired insurance certificate. */
  warning?: string | null;
  /** The money line; leave it out where the money is not the viewer's to see. */
  money?: BookingCardMoney | null;
  /** Said in place of the money line when there is none ("Nothing booked yet"). */
  noMoneyText?: string;
  /** Before the title — the office's "select to email" box. */
  leading?: ReactNode;
  /** Beside the stage — the office's actions menu. Read-only cards have none. */
  actions?: ReactNode;
  /** More detail under the money: the vendor portal's day-of facts. */
  children?: ReactNode;
  className?: string;
}

/**
 * One booking as a card — the office's Vendors view on a phone, and the
 * vendor portal's own booking (read-only: no leading box, no actions). Takes
 * a plain view-model, so each side decides what it may show.
 */
export function BookingCard({
  title,
  description,
  stage,
  warning,
  money,
  noMoneyText,
  leading,
  actions,
  children,
  className = 'p-3 space-y-2',
}: BookingCardProps) {
  return (
    <div className={className}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          {leading}
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate">{title}</p>
            {description ? <p className="text-xs text-gray-500">{description}</p> : null}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <BookingStageBadge stage={stage} />
          {actions}
        </div>
      </div>
      {warning && <StatusBadge label={warning} color="red" />}
      {money ? (
        <div className="text-sm">
          <BookingMoneyLine money={money} />
        </div>
      ) : noMoneyText ? (
        <div className="text-sm">
          <span className="text-gray-400">{noMoneyText}</span>
        </div>
      ) : null}
      {children}
    </div>
  );
}
